import React, { useEffect, useRef, useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { useT } from '../contexts/LocaleProvider';

/* navigator.clipboard needs a secure context · the textarea trick keeps
 * the button working on plain-http previews and older browsers. */
async function writeClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // permission denied or no secure context · fall through to the legacy path
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/**
 * Small "copy to clipboard" icon button · shows a check mark for a
 * second and a half after a successful copy.
 *
 * Renders nothing when there is no value, so empty cells don't reserve
 * dead space. Sits at 40% opacity and lights up on row hover (the
 * parent needs the `group` class) · still visible on touch, where
 * hover never fires.
 */
export default function CopyButton({ value, label, size = 13, className = '' }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  if (!value) return null;

  const onClick = async (e) => {
    // The button often lives inside a mailto: link · don't navigate.
    e.preventDefault();
    e.stopPropagation();
    if (!(await writeClipboard(value))) return;
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1500);
  };

  const tip = copied ? t('common.copied') : (label || t('common.copy'));

  return (
    <button
      type="button"
      onClick={onClick}
      title={tip}
      aria-label={tip}
      className={`shrink-0 inline-flex items-center justify-center rounded p-1 transition
        ${copied
          ? 'text-[hsl(var(--accent))] opacity-100'
          : 'text-[hsl(var(--text-muted))] opacity-40 group-hover:opacity-100 focus-visible:opacity-100 hover:bg-[hsl(var(--surface-2))] hover:text-[hsl(var(--text))]'}
        ${className}`}
    >
      {copied
        ? <Check style={{ width: size, height: size }} />
        : <Copy style={{ width: size, height: size }} />}
    </button>
  );
}
